/* eslint-disable radix */
import React, { useState, useEffect, useContext } from 'react'
import Grid from '@material-ui/core/Grid'
import { Typography } from '@material-ui/core'

import moment, { Moment } from 'config/moment'
import { ReactComponent as EditIcon } from 'assets/icons/save.svg'
import { useApiCall, useCheckFormErrors } from 'hooks'
import { UserContext } from 'contexts'
import { getUserRole, Role } from 'utils/roles'
import { Dialog, UploadButton, snackbar } from 'components'
import { PrizeCategory } from 'consts/prize'
import { PartnerIntegration } from 'features/Main/components/PartnerIntegration'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { TabPanel } from 'components/Dialog/TabPanel'
import CustomTextField from 'components/CustomTextField'
import { ConfigUtils } from 'utils/config'

import { PrizeDateRangeFields } from '../../../Memberships/Prizes/components/PrizeDateRangeFields'
import { useStyles } from '../../../ManualRewards/components/CreateManualRewardDialog/styles'
import {
  campaignDialogStyle,
  voucherDialogContentStyle,
} from '../../../Campaigns/components/campaignDialogStyle'
import {
  getVoucherDialogTabs,
  getVoucherPopUpTabIndex,
  isCampaignPopUpsEnabled,
  shouldShowPushImageUpload,
} from '../../../Campaigns/components/campaignFeatureFlags'
import { fetchPushUserCount } from '../../../Campaigns/api'
import { DataParams, DataResponse } from '../../../Campaigns/types'
import { Tag } from '../../../Tags/types'
import { updateVoucher, getVoucher, uploadPopUpImage } from '../../api'
import {
  Voucher,
  UpdateVoucherResponse,
  UpdateVoucherData,
  GetVoucherResponse,
  UploadPopUpImageResponse,
} from '../../types'
import { TagOption, VoucherSegmentationTab } from '../VoucherSegmentationTab'
import { createNotification, updateNotification } from '../../../Notifications/api'
import {
  CreateNotificationPayload,
  CreateNotificationResponse,
  UpdateNotificationData,
  UpdateNotificationResponse,
} from '../../../Notifications/types'

interface ValidationData {
  toteatEnabled: boolean
  toteatCategory?: PrizeCategory
  pushMessage?: boolean
  activePopUp?: boolean
  hasPopUpImagePreview?: boolean
  isCampaignPopUpsEnabled?: boolean
}

const rules = {
  listingTitle: [
    {
      validate: (value: string) => value.trim().length > 0,
      message: 'El título es requerido',
      validateEmpty: true,
    },
    { validate: (value: string) => value.length <= 70, message: 'Máximo 70 caracteres' },
  ],
  shortTitle: [
    {
      validate: (shortTitle: string) => shortTitle.trim().length > 0,
      message: 'La descripción es requerida',
      validateEmpty: true,
    },
    { validate: (shortTitle: string) => shortTitle.length <= 40, message: 'Máximo 40 caracteres' },
  ],
  benefitTitle: [
    {
      validate: (value: string) => value.trim().length > 0,
      message: 'El título del beneficio es requerido',
      validateEmpty: true,
    },
    { validate: (value: string) => value.length <= 70, message: 'Máximo 70 caracteres' },
  ],
  additionalText: [
    {
      validate: (value: string) => value.trim().length > 0,
      message: 'El título secundario es requerido',
      validateEmpty: true,
    },
    { validate: (value: string) => value.length <= 100, message: 'Máximo 100 caracteres' },
  ],
  prizeStartDate: [
    {
      validate: (value: Moment | null) => !!value,
      message: 'La fecha de inicio es requerida',
      validateEmpty: true,
    },
  ],
  prizeExpiration: [
    {
      validate: (value: Moment | null) => !!value,
      message: 'La fecha de vencimiento es requerida',
      validateEmpty: true,
    },
  ],
  pushTitle: [
    {
      validate: (title: string, data?: ValidationData) => {
        if (data?.pushMessage && !title.trim()) return false
        return true
      },
      message: 'El título push es requerido',
      validateEmpty: true,
    },
  ],
  pushDescription: [
    {
      validate: (description: string, data?: ValidationData) => {
        if (data?.pushMessage && !description.trim()) return false
        return true
      },
      message: 'La descripción push es requerida',
      validateEmpty: true,
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        if (data.toteatEnabled && data.toteatCategory === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID de beneficio POS es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        if (data.toteatEnabled && data.toteatCategory === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
  popUpImageId: [
    {
      validate: (value: string | undefined, data: ValidationData) => {
        const { activePopUp, hasPopUpImagePreview, isCampaignPopUpsEnabled: popUpsEnabled } = data
        if (popUpsEnabled && activePopUp && !value?.trim() && !hasPopUpImagePreview) {
          return false
        }
        return true
      },
      message: 'La imagen del pop-up es requerida',
      validateEmpty: true,
    },
  ],
}

interface UpdateVoucherDialogProps {
  handleClose: () => void
  onDone: (updatedVoucher: Voucher) => void
  voucherToUpdate: Voucher
  tags: Tag[]
}

const UpdateVoucherDialog: React.FC<UpdateVoucherDialogProps> = ({
  handleClose,
  onDone,
  voucherToUpdate,
  tags,
}) => {
  const classes = useStyles()
  const tabs = getVoucherDialogTabs()
  const popUpTabIndex = getVoucherPopUpTabIndex()
  const campaignPopUpsEnabled = isCampaignPopUpsEnabled()
  const { admin } = useContext(UserContext)
  const userRole = getUserRole(admin)
  const isMarketing = userRole === Role.Marketing

  const ignoreVoucherDetailRefreshError = () => {
    // Mantener preview del listado si falla el GET del voucher
  }

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))

  const existingNotification = voucherToUpdate.notification
  const [listingTitle, setListingTitle] = useState(
    voucherToUpdate.listingTitle || voucherToUpdate.title || '',
  )
  const [shortTitle, setShortTitle] = useState(voucherToUpdate.shortTitle)
  const [benefitTitle, setBenefitTitle] = useState(voucherToUpdate.title)
  const [additionalText, setAdditionalText] = useState(voucherToUpdate.additionalText || '')
  const [active, setActive] = useState(voucherToUpdate.active)
  const [pushMessage, setPushMessage] = useState(voucherToUpdate.pushMessage)
  const [pushTitle, setPushTitle] = useState(
    existingNotification?.title || voucherToUpdate.pushTitle || '',
  )
  const [pushDescription, setPushDescription] = useState(
    existingNotification?.body || voucherToUpdate.pushDescription || '',
  )
  const [tagsSelected, setTagsSelected] = useState(
    voucherToUpdate.tags?.map(tag => ({ label: tag.title, value: tag.id })) || [],
  )
  const [excludedTagsSelected, setExcludedTagsSelected] = useState(
    voucherToUpdate.excludedTags?.map(tag => ({ label: tag.title, value: tag.id })) || [],
  )
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(voucherToUpdate.allTagsAreRequired)
  const [emailChips] = useState(voucherToUpdate.preloadedEmails || [])
  const [prizeStartDate, setPrizeStartDate] = useState<Moment | null>(
    voucherToUpdate.startDate ? moment(voucherToUpdate.startDate) : null,
  )
  const [prizeExpiration, setPrizeExpiration] = useState<Moment | null>(
    voucherToUpdate.expiration ? moment(voucherToUpdate.expiration) : null,
  )
  const [iconPreview, setIconPreview] = useState(voucherToUpdate.iconUrl)
  const [imagePreview, setImagePreview] = useState(voucherToUpdate.imageUrl)
  const [activePopUp, setActivePopUp] = useState(voucherToUpdate.activePopUp ?? false)
  const [popUpImageId, setPopUpImageId] = useState(voucherToUpdate.popUpImageId || '')
  const [popUpImagePreview, setPopUpImagePreview] = useState(voucherToUpdate.popUpImageUrl || '')
  const [isUploadingPopUpImage, setIsUploadingPopUpImage] = useState(false)
  const [storesEnabled, setStoresEnabled] = useState(voucherToUpdate.storesEnabled)
  const [tucanEnabled, setTucanEnabled] = useState(voucherToUpdate.tucanEnabled)
  const [toteatEnabled, setToteatEnabled] = useState(voucherToUpdate.toteatEnabled)
  const [tucanCategory, setTucanCategory] = useState(voucherToUpdate.tucanCategory)
  const [toteatCategory, setToteatCategory] = useState(voucherToUpdate.toteatCategory)
  const [toteatBenefitId, setToteatBenefitId] = useState(voucherToUpdate.toteatBenefitId || '')
  const [skusList, setSkusList] = useState(voucherToUpdate.skus?.map(sku => sku.sku) || [])
  const [currentSku, setCurrentSku] = useState('')
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)
  const [userCount, setUserCount] = useState<number | null>(null)
  const [tabIndex, setTabIndex] = useState(0)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  const hasAnyIntegration = ConfigUtils.hasAnyIntegrationEnabled()
  const pushAlreadySent = Boolean(existingNotification?.sent)
  const isPushLocked = Boolean(voucherToUpdate.executedAt) || pushAlreadySent

  const [updateVoucherApi, isLoading] = useApiCall<UpdateVoucherData, UpdateVoucherResponse>(
    updateVoucher,
  )
  const [getVoucherApi] = useApiCall<number, GetVoucherResponse>(getVoucher)
  const [uploadPopUpImageApi] = useApiCall<File, UploadPopUpImageResponse>(uploadPopUpImage)
  const [updateNotificationApi] = useApiCall<UpdateNotificationData, UpdateNotificationResponse>(
    updateNotification,
  )
  const [createNotificationApi] = useApiCall<CreateNotificationPayload, CreateNotificationResponse>(
    createNotification,
  )
  const [requestData, isRequestingData] = useApiCall<DataParams, DataResponse>(fetchPushUserCount)

  const handleSetTags = async (selectedTags: TagOption[]) => {
    setTagsSelected(selectedTags)
  }

  const handleSetExcludedTags = async (selectedTags: TagOption[]) => {
    setExcludedTagsSelected(selectedTags)
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    setAllTagsAreRequired(!allTagsAreRequired)
  }

  const handleFetchUserCount = async () => {
    if (!tagsSelected.length && !excludedTagsSelected.length) {
      setUserCount(null)
      return
    }

    try {
      const tagsSelectedId = tagsSelected.map(tag => tag.value)
      const data = await requestData({
        tagIds: tagsSelectedId,
        excludedTagIds: excludedTagsSelected.map(tag => tag.value),
        allTagsAreRequired,
      })
      setUserCount(data.count)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo calcular la cantidad de usuarios. Intente de nuevo.')
    }
  }

  useEffect(() => {
    handleFetchUserCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tagsSelected, excludedTagsSelected, allTagsAreRequired])

  useEffect(() => {
    if (voucherToUpdate.iconUrl) {
      setIconPreview(voucherToUpdate.iconUrl)
    }
    if (voucherToUpdate.imageUrl) {
      setImagePreview(voucherToUpdate.imageUrl)
    }
    if (voucherToUpdate.popUpImageId && !popUpImageId) {
      setPopUpImageId(voucherToUpdate.popUpImageId)
    }
    if (voucherToUpdate.popUpImageUrl && !popUpImagePreview) {
      setPopUpImagePreview(voucherToUpdate.popUpImageUrl)
    }

    getVoucherApi(voucherToUpdate.id)
      .then(({ voucher }) => {
        if (voucher.iconUrl) setIconPreview(voucher.iconUrl)
        if (voucher.imageUrl) setImagePreview(voucher.imageUrl)
        if (voucher.popUpImageId) setPopUpImageId(voucher.popUpImageId)
        if (voucher.popUpImageUrl) setPopUpImagePreview(voucher.popUpImageUrl)
        if (voucher.activePopUp !== undefined) setActivePopUp(voucher.activePopUp)
      })
      .catch(ignoreVoucherDetailRefreshError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherToUpdate.id])

  const handlePopUpImageChange = async (file: File) => {
    const localPreviewUrl = URL.createObjectURL(file)
    setPopUpImagePreview(localPreviewUrl)
    setIsUploadingPopUpImage(true)
    try {
      const { popUpImageId: uploadedId, popUpImageUrl } = await uploadPopUpImageApi(file)
      setPopUpImageId(uploadedId)
      if (popUpImageUrl) {
        URL.revokeObjectURL(localPreviewUrl)
        setPopUpImagePreview(popUpImageUrl)
      }
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo subir la imagen del pop-up. Intente de nuevo.')
      URL.revokeObjectURL(localPreviewUrl)
      setPopUpImagePreview(voucherToUpdate.popUpImageUrl || '')
      setPopUpImageId(voucherToUpdate.popUpImageId || '')
    } finally {
      setIsUploadingPopUpImage(false)
    }
  }

  const fields = {
    listingTitle,
    shortTitle,
    benefitTitle,
    additionalText,
    pushMessage,
    pushTitle,
    pushDescription,
    prizeStartDate,
    prizeExpiration,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    toteatCategory,
    activePopUp,
    popUpImageId,
    hasPopUpImagePreview: !!(popUpImagePreview || voucherToUpdate.popUpImageId),
    isCampaignPopUpsEnabled: campaignPopUpsEnabled,
  }

  const { hasErrors, errors } = useCheckFormErrors(fields, rules, [
    'listingTitle',
    'shortTitle',
    'benefitTitle',
    'additionalText',
    'prizeStartDate',
    'prizeExpiration',
    ...(pushMessage ? ['pushTitle', 'pushDescription'] : []),
    ...(campaignPopUpsEnabled && activePopUp ? ['popUpImageId'] : []),
  ])

  const isSubmitDisabled = hasErrors || isLoading || isUploadingPopUpImage || hasPartnerErrors

  const onUpdateVoucher = async () => {
    setHasAttemptedSubmit(true)
    if (!prizeStartDate || !prizeExpiration) return
    if (!isPushLocked && pushMessage && (!pushTitle.trim() || !pushDescription.trim())) {
      snackbar.show('Todos los campos de la push son requeridos.')
      return
    }

    try {
      let { notificationId } = voucherToUpdate

      if (pushMessage && !isPushLocked) {
        const tagsSelectedId =
          tagsSelected.map(tag => tag.value).length > 0
            ? tagsSelected.map(tag => tag.value)
            : voucherToUpdate.tags?.map(tag => tag.id) || []
        const scheduled = prizeStartDate.isAfter(moment())
          ? prizeStartDate.toISOString()
          : moment()
              .add(1, 'minute')
              .toISOString()

        if (existingNotification && voucherToUpdate.notificationId) {
          const notificationPayload: UpdateNotificationData = {
            id: voucherToUpdate.notificationId,
            payload: {
              title: pushTitle,
              body: pushDescription,
              scheduled,
              tagIds: tagsSelectedId,
              allTagsAreRequired,
              visitsOperator: '==',
              rewardInverted: false,
            },
          }
          await updateNotificationApi(notificationPayload)
          notificationId = voucherToUpdate.notificationId
        } else {
          const notificationPayload: CreateNotificationPayload = {
            title: pushTitle,
            body: pushDescription,
            scheduled,
            tagIds: tagsSelectedId,
            allTagsAreRequired,
            visitsOperator: '==',
            rewardInverted: false,
          }
          const { notification } = await createNotificationApi(notificationPayload)
          notificationId = notification.id
        }
      } else if (pushMessage && isPushLocked) {
        notificationId = voucherToUpdate.notificationId
      }

      const { voucher } = await updateVoucherApi({
        id: voucherToUpdate.id,
        payload: {
          listingTitle,
          shortTitle,
          title: benefitTitle,
          additionalText,
          ...(isMarketing ? {} : { active }),
          pushMessage: isPushLocked ? voucherToUpdate.pushMessage : pushMessage,
          pushTitle: isPushLocked
            ? existingNotification?.title || voucherToUpdate.pushTitle
            : pushTitle,
          pushDescription: isPushLocked
            ? existingNotification?.body || voucherToUpdate.pushDescription
            : pushDescription,
          notificationId: notificationId || undefined,
          prizeStartDate: prizeStartDate.toISOString(),
          prizeExpiration: prizeExpiration.toISOString(),
          storesEnabled,
          tucanEnabled,
          toteatEnabled,
          tucanCategory,
          toteatCategory,
          skus: skusList,
          toteatBenefitId,
          visitsOperator: '==',
          rewardInverted: false,
          ...(campaignPopUpsEnabled
            ? {
                activePopUp,
                popUpImageId: activePopUp
                  ? popUpImageId || voucherToUpdate.popUpImageId || undefined
                  : undefined,
              }
            : {}),
        },
      })
      onDone(voucher)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo actualizar el voucher. Intente de nuevo.')
    }
  }

  return (
    <Dialog
      title="Editar voucher"
      isOpen
      maxWidth={false}
      showTabsActions
      onCancel={handleClose}
      cancelButtonText="Cancelar"
      isLoading={isLoading}
      okButtonText="Guardar"
      okButtonProps={{ disabled: isSubmitDisabled, startIcon: <EditIcon /> }}
      onAccept={onUpdateVoucher}
      tabs={tabs}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
      showUserCount={tagsSelected.length > 0 || excludedTagsSelected.length > 0}
      isRequestingData={isRequestingData}
      userCount={userCount ?? 0}
      style={campaignDialogStyle}
      contentStyle={voucherDialogContentStyle}
    >
      <Grid>
        <TabPanel value={tabIndex} index={0} id="voucher">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomTextField
                label="Título*"
                value={listingTitle}
                fullWidth
                onChange={e => setListingTitle(e.target.value)}
                disabled={isLoading}
                error={hasAttemptedSubmit && errors.listingTitle?.hasError}
                helperText={hasAttemptedSubmit ? errors.listingTitle?.message : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                label="Descripción*"
                value={shortTitle}
                fullWidth
                onChange={e => setShortTitle(e.target.value)}
                disabled={isLoading}
                multiline
                rows={3}
                error={hasAttemptedSubmit && errors.shortTitle?.hasError}
                helperText={hasAttemptedSubmit ? errors.shortTitle?.message : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <PrizeDateRangeFields
                startDate={prizeStartDate}
                expiration={prizeExpiration}
                onStartDateChange={setPrizeStartDate}
                onExpirationChange={setPrizeExpiration}
                disabled={isLoading}
                requiredStartDate
                requiredExpiration
                enforceStartNotBeforeToday={false}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={() => setActive(!active)}
                    checked={active}
                    disabled={isLoading || isMarketing}
                  />
                }
                label="Habilitar voucher"
              />
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabIndex} index={1} id="segmentar">
          <VoucherSegmentationTab
            tagsForSelect={tagsForSelect}
            tagsSelected={tagsSelected}
            onTagsChange={handleSetTags}
            excludedTagsSelected={excludedTagsSelected}
            onExcludedTagsChange={handleSetExcludedTags}
            allTagsAreRequired={allTagsAreRequired}
            onAllTagsAreRequiredChange={handleAllTagsAreRequiredChange}
            emailChips={emailChips}
            onEmailChipsChange={() => undefined}
            onEmailCsvChange={() => undefined}
            onEmailCsvClear={() => undefined}
            emailCsvFile={null}
            csvEmailCount={emailChips.length}
            isLoading={isLoading}
            readOnly
          />
        </TabPanel>

        <TabPanel value={tabIndex} index={2} id="beneficio">
          <Grid container spacing={3}>
            <Grid item xs={12} className={classes.topCheckbox}>
              <CustomFormControlLabel
                control={<CustomCheckbox checked disabled />}
                label="Activar Reward"
              />
              <Typography
                variant="caption"
                display="block"
                style={{ marginLeft: 40, marginTop: 4 }}
              >
                Los vouchers son siempre de uso único.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                label="Título*"
                value={benefitTitle}
                fullWidth
                onChange={e => setBenefitTitle(e.target.value)}
                disabled={isLoading}
                error={hasAttemptedSubmit && errors.benefitTitle?.hasError}
                helperText={hasAttemptedSubmit ? errors.benefitTitle?.message : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <CustomTextField
                label="Título secundario*"
                value={additionalText}
                fullWidth
                onChange={e => setAdditionalText(e.target.value)}
                disabled={isLoading}
                error={hasAttemptedSubmit && errors.additionalText?.hasError}
                helperText={hasAttemptedSubmit ? errors.additionalText?.message : ''}
              />
            </Grid>

            <Grid item xs={12}>
              <Grid container className={classes.iconUploadContainer}>
                <Grid item xs={12}>
                  <Typography className={classes.dialogSubTitle}>Icono del reward</Typography>
                  <div className={classes.iconUploadPreviewContainer}>
                    {iconPreview ? (
                      <img src={iconPreview} alt="" className={classes.iconPreview} />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        No hay vista previa del icono.
                      </Typography>
                    )}
                  </div>
                </Grid>
              </Grid>
            </Grid>

            {hasAnyIntegration && (
              <>
                <Grid item xs={12}>
                  <Typography className={classes.dialogSubTitle} style={{ marginTop: 20 }}>
                    Integración
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <PartnerIntegration
                    hideTitle
                    storesEnabled={storesEnabled}
                    setStoresEnabled={setStoresEnabled}
                    tucanEnabled={tucanEnabled}
                    setTucanEnabled={setTucanEnabled}
                    toteatEnabled={toteatEnabled}
                    setToteatEnabled={setToteatEnabled}
                    tucanCategory={tucanCategory}
                    setTucanCategory={setTucanCategory}
                    toteatCategory={toteatCategory}
                    setToteatCategory={setToteatCategory}
                    currentSku={currentSku}
                    setCurrentSku={setCurrentSku}
                    skusList={skusList}
                    setSkusList={setSkusList}
                    toteatBenefitId={toteatBenefitId}
                    setToteatBenefitId={setToteatBenefitId}
                    errors={errors}
                    isLoading={isLoading}
                    onValidationChange={setHasPartnerErrors}
                    showErrors={hasAttemptedSubmit}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabIndex} index={3} id="push">
          <Grid container spacing={3}>
            {isPushLocked && (
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  La push no puede modificarse porque el voucher ya fue procesado y asignado a los
                  usuarios.
                </Typography>
              </Grid>
            )}
            <Grid item xs={12} className={classes.topCheckbox}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={() => setPushMessage(!pushMessage)}
                    checked={pushMessage}
                    disabled={isLoading || isPushLocked}
                  />
                }
                label="Sale con push?"
              />
            </Grid>
            {pushMessage && (
              <>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Título push*"
                    value={pushTitle}
                    fullWidth
                    onChange={e => setPushTitle(e.target.value)}
                    disabled={isLoading || isPushLocked}
                    error={hasAttemptedSubmit && errors.pushTitle?.hasError}
                    helperText={hasAttemptedSubmit ? errors.pushTitle?.message : ''}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Descripción push*"
                    value={pushDescription}
                    fullWidth
                    multiline
                    onChange={e => setPushDescription(e.target.value)}
                    disabled={isLoading || isPushLocked}
                    error={hasAttemptedSubmit && errors.pushDescription?.hasError}
                    helperText={hasAttemptedSubmit ? errors.pushDescription?.message : ''}
                  />
                </Grid>
                {shouldShowPushImageUpload() && (
                  <Grid item xs={12}>
                    <Grid container className={classes.iconUploadContainer}>
                      <Grid item xs={6} className={classes.iconUploadButton}>
                        <UploadButton
                          id="voucher-push-image-update-button"
                          accept=".jpg, .jpeg, .png"
                          label="Cambiar imagen de la push"
                          onChange={file => {
                            setImagePreview(URL.createObjectURL(file))
                          }}
                          maxSizeInMB={2}
                          disabled={isLoading || isPushLocked}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <div className={classes.iconUploadPreviewContainer}>
                          <img src={imagePreview} alt="" className={classes.iconPreview} />
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </TabPanel>

        {campaignPopUpsEnabled && (
          <TabPanel value={tabIndex} index={popUpTabIndex} id="pop-up">
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.topCheckbox}>
                <CustomFormControlLabel
                  control={
                    <CustomCheckbox
                      onChange={e => setActivePopUp(e.target.checked)}
                      checked={activePopUp}
                      disabled={isLoading}
                    />
                  }
                  label="Activar Pop-up"
                />
              </Grid>

              {activePopUp && (
                <Grid item xs={12}>
                  <Grid container className={classes.iconUploadContainer}>
                    <Grid item xs={6} className={classes.iconUploadButton}>
                      <UploadButton
                        id="voucher-pop-up-image-update-button"
                        accept=".jpg, .jpeg, .png, .webp"
                        label="Subir imagen del pop-up*"
                        onChange={handlePopUpImageChange}
                        maxSizeInMB={2}
                        disabled={isLoading || isUploadingPopUpImage}
                      />
                      <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                        Formato vertical recomendado 9:16. Máximo 2MB (PNG, JPG, WebP).
                      </Typography>
                      {hasAttemptedSubmit && errors.popUpImageId?.hasError && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ marginTop: 4, display: 'block' }}
                        >
                          {errors.popUpImageId.message}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.iconUploadPreviewContainer}>
                        {popUpImagePreview && (
                          <img
                            src={popUpImagePreview}
                            alt=""
                            className={classes.iconPreview}
                            style={{ aspectRatio: '9 / 16', objectFit: 'cover' }}
                          />
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </TabPanel>
        )}
      </Grid>
    </Dialog>
  )
}

export { UpdateVoucherDialog }
